import { GlTooltipDirective, GlBadge, GlButtonGroup, GlButton, GlIcon, GlSearchBoxByType } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { mapActions, mapState } from '../../../../../node_modules/pinia/dist/pinia.mjs?vue3';
import micromatch from 'micromatch';
import { s__, sprintf } from '~/locale';
import { RecycleScroller } from '../../../../../vendor/assets/javascripts/vue-virtual-scroller/src/index.js?vue3';
import { isElementClipped } from '~/lib/utils/common_utils';
import { MR_FOCUS_FILE_BROWSER } from '~/behaviors/shortcuts/keybindings';
import { useCodeReview } from '../stores/code_review.js?vue3';
import { useFileBrowser } from '../stores/file_browser.js?vue3';
import FileRow from '../../vue_shared/components/file_row.vue?vue3';
import FileRowStats from '~/diffs/components/file_row_stats.vue';
export default {
    name: 'TreeList',
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        FileRowStats,
        GlBadge,
        GlButtonGroup,
        GlButton,
        GlIcon,
        FileRow,
        RecycleScroller,
        GlSearchBoxByType
    },
    props: {
        hideFileStats: {
            type: Boolean,
            required: true
        },
        loadedFiles: {
            type: Object,
            required: false,
            default: null
        },
        totalFilesCount: {
            type: [
                Number,
                String
            ],
            default: undefined,
            required: false
        },
        rowHeight: {
            type: Number,
            required: true
        },
        groupBlobsListItems: {
            type: Boolean,
            required: false,
            default: true
        },
        currentDiffFileId: {
            type: String,
            required: false,
            default: null
        },
        linkedFilePath: {
            type: String,
            required: false,
            default: null
        }
    },
    emits: [
        'click-file',
        'toggle-folder'
    ],
    data () {
        return {
            search: ''
        };
    },
    computed: {
        ...mapState(useFileBrowser, [
            'renderTreeList',
            'tree',
            'allBlobs',
            'flatBlobsList',
            'focusSearchRequest'
        ]),
        ...mapState(useCodeReview, [
            'reviewedIds'
        ]),
        flatUngroupedList () {
            return this.flatBlobsList.reduce((acc, blob, index)=>{
                const previous = acc[acc.length - 1];
                const adjacentNonHeader = previous?.isHeader ? acc[acc.length - 2] : previous;
                const isSibling = adjacentNonHeader?.parentPath === blob.parentPath;
                if (!isSibling) {
                    acc.push({
                        key: `header-${index}`,
                        path: blob.parentPath,
                        isHeader: true,
                        tree: [],
                        level: 0
                    });
                }
                acc.push({
                    ...blob,
                    level: 1
                });
                return acc;
            }, []);
        },
        filteredTreeList () {
            let search = this.search.toLowerCase().trim();
            if (search === '') {
                return this.renderTreeList ? this.tree : this.allBlobs;
            }
            const searchSplit = search.split(',').filter((t)=>t);
            if (searchSplit.length > 1) {
                search = `(${searchSplit.map((s)=>s.replace(/(^ +| +$)/g, '')).join('|')})`;
            } else {
                [search] = searchSplit;
            }
            return this.allBlobs.reduce((acc, folder)=>{
                const tree = folder.tree.filter((f)=>micromatch.contains(f.path, search, {
                        nocase: true
                    }));
                if (tree.length) {
                    return acc.concat({
                        ...folder,
                        tree
                    });
                }
                return acc;
            }, []);
        },
        // Flatten the treeList so there's no nested trees
        // This gives us fixed row height for virtual scrolling
        // in:  [{ path: 'a', tree: [{ path: 'b' }] }, { path: 'c' }]
        // out: [{ path: 'a', tree: [{ path: 'b' }] }, { path: 'b' }, { path: 'c' }]
        flatFilteredTreeList () {
            const result = [];
            const createFlatten = (level, hidden)=>(item)=>{
                    const isTree = item.type === 'tree';
                    result.push({
                        ...item,
                        hidden,
                        level: item.isHeader ? 0 : level,
                        key: item.key || item.path
                    });
                    const isHidden = hidden || isTree && !item.opened;
                    item.tree.forEach(createFlatten(level + 1, isHidden));
                };
            this.filteredTreeList.forEach(createFlatten(0));
            return result;
        },
        flatListWithLinkedFile () {
            const result = [
                ...this.flatFilteredTreeList
            ];
            const linkedFileIndex = result.findIndex((item)=>item.path === this.linkedFilePath);
            const [linkedFileItem] = result.splice(linkedFileIndex, 1);
            if (linkedFileItem.parentPath === '/') return [
                {
                    ...linkedFileItem,
                    level: 0,
                    linked: true,
                    hidden: false
                },
                ...result
            ];
            // remove detached folder from the tree
            const next = result[linkedFileIndex];
            const prev = result[linkedFileIndex - 1];
            const hasContainingFolder = prev && prev.type === 'tree' && prev.level === linkedFileItem.level - 1;
            const hasSibling = next && next.type !== 'tree' && next.level === linkedFileItem.level;
            if (hasContainingFolder && !hasSibling) {
                // folder tree is always condensed so we only need to remove the parent folder
                result.splice(linkedFileIndex - 1, 1);
            }
            return [
                {
                    level: 0,
                    key: 'linked-path',
                    isHeader: true,
                    opened: true,
                    path: linkedFileItem.parentPath,
                    type: 'tree',
                    hidden: false
                },
                {
                    ...linkedFileItem,
                    level: 1,
                    linked: true,
                    hidden: false
                },
                ...result
            ];
        },
        treeList () {
            if (!this.renderTreeList && !this.groupBlobsListItems && !this.search) return this.flatUngroupedList;
            const list = this.linkedFilePath ? this.flatListWithLinkedFile : this.flatFilteredTreeList;
            if (this.search) return list;
            return list.filter((item)=>!item.hidden);
        }
    },
    watch: {
        focusSearchRequest () {
            this.$refs.search.focusInput();
        },
        currentDiffFileId: {
            async handler (hash) {
                if (!hash) return;
                this.openFileTree(hash);
                await this.$nextTick();
                this.showSelectedItem(hash);
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions(useFileBrowser, [
            'setRenderTreeList',
            'setTreeOpen'
        ]),
        showSelectedItem (hash) {
            // let the layout stabilize, we need to wait for:
            // scroll to file, sticky elements update, file browser height update
            // file browser height might be shrunk, so we need to scroll to the selected file
            setTimeout(()=>{
                const { scroller } = this.$refs;
                if (!scroller) return;
                const itemElement = this.$el.querySelector(`[data-file-row="${hash}"]`);
                if (!itemElement) {
                    scroller.scrollToItem(this.treeList.findIndex((item)=>item.fileHash === hash));
                } else if (isElementClipped(itemElement, scroller.$el)) {
                    itemElement.scrollIntoView({
                        block: 'nearest',
                        behavior: 'instant'
                    });
                }
            }, 20);
        },
        openFileTree (hash) {
            const file = this.flatFilteredTreeList.find((f)=>f.fileHash === hash);
            if (file) {
                file.path.split('/').slice(0, -1).reduce((acc, part)=>[
                        ...acc,
                        acc.length ? `${acc.at(-1)}/${part}` : part
                    ], []).forEach((path)=>this.setTreeOpen(path, true));
            }
        },
        isLoading (item) {
            if (item.isHeader || item.type === 'tree') return false;
            return Boolean(this.loadedFiles) && !this.loadedFiles[item.fileHash];
        },
        showStats (item) {
            return !this.hideFileStats && item.type === 'blob' && Boolean(item.addedLines || item.removedLines);
        },
        isViewed (item) {
            return this.reviewedIds[item.codeReviewId] ?? this.reviewedIds[item.id];
        },
        // per-row state changes on every streamed file; resolving it here touches only the rows the
        // scroller renders instead of rebuilding the whole list
        rowFile (item) {
            if (item.isHeader || item.type === 'tree') return item;
            return {
                ...item,
                loading: this.isLoading(item),
                active: item.fileHash === this.currentDiffFileId,
                viewed: this.isViewed(item)
            };
        }
    },
    searchPlaceholder: sprintf(s__('MergeRequest|Search (e.g. *.vue) (%{SHORTCUT})'), {
        SHORTCUT: MR_FOCUS_FILE_BROWSER.defaultKeys[0].toUpperCase()
    })
};
